// Search drawer: opens from any [data-open-search] link (which stays a normal link to the search page without
// script), fetches product suggestions as you type, and fills the field from the trending terms. A custom element so
// it starts again cleanly when the theme editor re-renders the header.
import { debounce } from '@theme/utilities';
import { openDialog, setupDialog } from '@purolabs/dialog';

class SearchDrawer extends HTMLElement {
  #events;
  #request;

  connectedCallback() {
    this.#events = new AbortController();
    const { signal } = this.#events;
    this.dialog = this.querySelector('dialog');
    this.input = this.querySelector('.pl-search__input');
    this.results = this.querySelector('.pl-search__results');
    this.suggestions = this.querySelector('.pl-search__suggestions');
    this.footer = this.querySelector('.pl-search__footer');
    this.status = this.querySelector('[data-search-status]');
    this.debouncedSearch = debounce(() => this.#search(), 250);

    setupDialog(this.dialog, signal);

    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-open-search]');
      if (!opener) return;
      event.preventDefault();
      openDialog(this.dialog, opener);
      this.input.focus();
    }, { signal });

    this.dialog.addEventListener('click', (event) => {
      if (event.target.closest('[data-close-search]')) this.dialog.close();
      const term = event.target.closest('[data-term]');
      if (term) {
        this.input.value = term.dataset.term;
        // The button is about to be hidden with the suggestions, so keep focus in the field
        this.input.focus();
        this.#search();
      }
    }, { signal });

    this.input.addEventListener('input', () => this.debouncedSearch(), { signal });
    this.dialog.addEventListener('close', () => this.#reset(), { signal });
  }

  disconnectedCallback() {
    this.#events?.abort();
    this.#request?.abort();
    this.debouncedSearch?.cancel?.();
  }

  #showSuggestions() {
    this.results.hidden = true;
    this.results.replaceChildren();
    this.suggestions.hidden = false;
    if (this.footer) this.footer.hidden = true;
    this.#announce('');
  }

  #reset() {
    this.#request?.abort();
    this.debouncedSearch.cancel?.();
    this.input.value = '';
    this.#showSuggestions();
  }

  async #search() {
    const terms = this.input.value.trim();
    this.#request?.abort();
    if (!terms) return this.#showSuggestions();

    const request = (this.#request = new AbortController());
    const params = new URLSearchParams({
      q: terms,
      'resources[type]': 'product',
      'resources[limit]': '10',
      'resources[options][unavailable_products]': 'hide',
      'resources[options][fields]': 'title,body,product_type,variants.title,variants.sku,vendor,tag',
      section_id: 'purolabs-predictive-search',
    });
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}search/suggest?${params}`, { signal: request.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      if (request.signal.aborted) return;
      const section = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      this.results.replaceChildren(...(section?.childNodes ?? []));
      this.results.hidden = false;
      this.suggestions.hidden = true;
      if (this.footer) this.footer.hidden = false;
      this.#announce(this.results.querySelector('[data-search-message]')?.textContent.trim() ?? '');
    } catch (error) {
      // Leave what is showing; pressing Enter still goes to the full search page
      if (error.name !== 'AbortError') console.warn('Search suggestions:', error);
    }
  }

  // One short line for screen readers instead of reading out the whole results list
  #announce(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('pl-search-drawer')) customElements.define('pl-search-drawer', SearchDrawer);
